import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, ShieldCheck, Lock, Scale, CreditCard, BadgeCheck, Heart, ChevronDown, Users, FileText, ScrollText, Landmark, Flag } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CocoonMark } from "@/components/CocoonMark";
import { InstallButton } from "@/components/InstallButton";

const TRUST_ICONS = { rgpd: ShieldCheck, cyber: Lock, dsa: Scale, pay: CreditCard, lcen: BadgeCheck };
const RADIO_MINI = { fr: ["En direct", "Écouter la radio"], en: ["Live", "Listen to the radio"], es: ["En directo", "Escuchar la radio"], nl: ["Live", "Luister naar de radio"] };

// Column that collapses on mobile (accordion) but is always expanded on desktop (md+).
function AccordionCol({ title, testId, children, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testId}
        className="w-full flex items-center justify-between md:pointer-events-none md:cursor-default py-1 md:py-0 md:mb-4"
        aria-expanded={open}
      >
        <span className="uppercase tracking-[0.22em] text-[0.65rem] text-foreground/40">{title}</span>
        <ChevronDown size={16} className={`text-foreground/40 md:hidden transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`${open ? "block" : "hidden"} md:block pt-1 md:pt-0`}>{children}</div>
    </div>
  );
}

const EXPLORE_LINKS = [
  { to: "/manifeste", key: "manifeste", id: "footer-manifeste" },
  { to: "/ecole-cocoon", key: "ecole", id: "footer-ecole" },
  { to: "/echo", key: "echo", id: "footer-echo" },
  { to: "/enfants", key: "enfants", id: "footer-enfants" },
  { to: "/radio", key: "derive", id: "footer-radio" },
  { to: "/tarifs", key: "tarifs", id: "footer-tarifs" },
  { to: "/faq", key: "faq", id: "footer-faq" },
];

const LEGAL_LINKS = [
  { to: "/legal/apropos", key: "nav.apropos", id: "footer-legal-apropos", icon: Users },
  { to: "/legal/confidentialite", key: "legal.confidentialite", id: "footer-legal-confidentialite", icon: ShieldCheck },
  { to: "/legal/cgu", key: "legal.cgu", id: "footer-legal-cgu", icon: FileText },
  { to: "/legal/charte", key: "legal.charte", id: "footer-legal-charte", icon: Heart },
  { to: "/legal/cgv", key: "legal.cgv", id: "footer-legal-cgv", icon: ScrollText },
  { to: "/legal/mentions", key: "legal.mentions", id: "footer-legal-mentions", icon: Landmark },
  { to: "/legal/dsa", key: "legal.dsa", id: "footer-legal-dsa", icon: Scale },
  { to: "/signalement", key: "legal.report", id: "footer-report", icon: Flag },
];

// === PARTENAIRES — bloc retiré de la homepage pour le moment ===
// Pour réactiver : réintégrer la liste PARTNERS + le bloc "footer-partners" (voir historique git).

export function Footer() {
  const { t, i18n } = useTranslation();
  const email = t("contact.email");
  const [legalOpen, setLegalOpen] = useState(false);
  const trustItems = (() => { try { return t("trust.items", { returnObjects: true }) || []; } catch { return []; } })();

  return (
    <footer className="relative overflow-hidden border-t border-white/10" data-testid="site-footer">
      {/* cinematic gold halo */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-48 bg-[radial-gradient(65%_140%_at_50%_0%,hsl(28_96%_56%/0.12),transparent_70%)]" />
      <div aria-hidden className="pointer-events-none absolute -top-px left-1/2 -translate-x-1/2 h-px w-2/3 bg-gradient-to-r from-transparent via-primary/50 to-transparent" />

      <div className="relative max-w-6xl mx-auto px-6 py-10 sm:py-16 flex flex-col gap-8 sm:gap-12">
        {/* main grid */}
        <div className="grid gap-8 sm:gap-10 md:gap-8 md:grid-cols-12">
          {/* brand */}
          <div className="md:col-span-4">
            <span className="flex items-center gap-2.5">
              <img
                src="/cocoon-logo-glow.png"
                alt="COCOON MIND"
                data-testid="footer-logo-mark"
                className="h-10 w-auto select-none transition-transform duration-500 hover:scale-105"
                style={{ filter: "drop-shadow(0 0 9px hsl(28 96% 56% / 0.35))" }}
              />
              <span className="font-display text-xl"><CocoonMark withMind /></span>
            </span>
            <p className="text-sm text-foreground/50 mt-4 leading-relaxed max-w-xs">{t("landing.footerTagline")}</p>

            {/* brand seal — links to manifesto */}
            <Link
              to="/manifeste"
              data-testid="footer-seal"
              className="group mt-6 inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/[0.06] pl-2.5 pr-4 py-1.5 backdrop-blur-sm transition-all duration-300 hover:border-primary/50 hover:-translate-y-0.5"
            >
              <span className="w-6 h-6 rounded-full bg-primary/12 border border-primary/25 flex items-center justify-center flex-shrink-0 transition-colors group-hover:bg-primary/20">
                <BadgeCheck size={13} className="text-primary" />
              </span>
              <span className="text-[0.72rem] tracking-wide text-primary/85 italic group-hover:text-primary transition-colors">{t("landing.footer.seal")}</span>
            </Link>

            {/* Parrainage / invitation à imprimer */}
            <Link
              to="/invitation"
              data-testid="footer-invitation"
              className="group mt-4 flex items-center gap-1.5 text-sm text-foreground/55 hover:text-primary transition-colors w-fit"
            >
              <span className="w-0 group-hover:w-3 h-px bg-primary/70 transition-all duration-300" />
              {i18n.language?.startsWith("en") ? "Invite a loved one" : i18n.language?.startsWith("es") ? "Invitar a un ser querido" : i18n.language?.startsWith("nl") ? "Nodig een dierbare uit" : "Inviter un proche"}
            </Link>
          </div>

          {/* explore */}
          <AccordionCol title={t("landing.footer.explore")} testId="footer-explore-toggle" className="md:col-span-2">
            <ul className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-sm sm:flex sm:flex-col sm:gap-2.5">
              {EXPLORE_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={l.id}
                    className="group inline-flex items-center gap-1.5 py-1 text-foreground/55 hover:text-primary transition-colors"
                  >
                    <span className="w-0 group-hover:w-3 h-px bg-primary/70 transition-all duration-300" />
                    {t(`nav.${l.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </AccordionCol>

          {/* contact + social */}
          <AccordionCol title={t("contact.label")} testId="footer-contact-toggle" className="md:col-span-2">
            <a href={`mailto:${email}`} className="inline-flex items-center gap-2 text-sm text-foreground/55 hover:text-primary transition-colors" data-testid="footer-contact">
              <Mail size={15} /> {email}
            </a>
            <p className="uppercase tracking-[0.22em] text-[0.65rem] text-foreground/40 mt-6 mb-3">{t("contact.follow")}</p>
            <button
              type="button"
              disabled
              title={t("contact.tiktokSoon")}
              aria-label={t("contact.tiktokSoon")}
              data-testid="footer-tiktok"
              className="inline-flex items-center gap-2 text-foreground/45 cursor-not-allowed w-fit"
            >
              <span className="w-9 h-9 rounded-full border border-white/10 bg-white/[0.03] flex items-center justify-center">
                <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden>
                  <path d="M16.5 3c.3 2.1 1.5 3.5 3.5 3.7v2.5c-1.2.1-2.4-.2-3.5-.8v5.9c0 3.4-2.7 5.7-5.8 5.2-2.6-.4-4.4-2.7-4.2-5.3.2-2.5 2.4-4.4 4.9-4.3.3 0 .6 0 .9.1v2.6c-.3-.1-.6-.2-.9-.2-1.2 0-2.2 1-2.2 2.2s1 2.2 2.2 2.2 2.2-1 2.2-2.2V3h2.9z" />
                </svg>
              </span>
              <span className="inline-flex items-center gap-1.5 text-sm">
                TikTok
                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-primary/15 text-primary/80">{t("contact.soonBadge")}</span>
              </span>
            </button>
            <InstallButton className="mt-4" testId="footer-install-btn" />
          </AccordionCol>

          {/* radio mini */}
          <div className="md:col-span-4">
            <Link to="/radio" data-testid="footer-radio-card" className="group block glass rounded-2xl border border-primary/15 p-5 hover:border-primary/40 transition-colors">
              <div className="flex items-center gap-4">
                <img src="/derive/radio/cover.jpg" alt="Radio Cocoon" className="w-16 h-16 rounded-xl object-cover border border-primary/25" />
                <div className="min-w-0">
                  <p className="inline-flex items-center gap-1.5 uppercase tracking-[0.22em] text-[0.62rem] text-red-300 mb-1"><span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" /> {(RADIO_MINI[(i18n.language || "fr").slice(0, 2)] || RADIO_MINI.fr)[0]}</p>
                  <h3 className="font-display italic text-gradient-orange text-xl leading-tight">Radio Cocoon</h3>
                  <p className="text-xs text-foreground/50 mt-1 group-hover:text-primary transition-colors">{(RADIO_MINI[(i18n.language || "fr").slice(0, 2)] || RADIO_MINI.fr)[1]} →</p>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* trust & compliance */}
        <div className="pt-8 sm:pt-10 border-t border-white/5" data-testid="footer-trust">
          <p className="text-center uppercase tracking-[0.24em] text-[0.65rem] text-foreground/35 mb-5 sm:mb-6">{t("trust.eyebrow")}</p>
          <div className="flex flex-wrap justify-center gap-3">
            {trustItems.map((it) => {
              const Icon = TRUST_ICONS[it.id] || ShieldCheck;
              const inner = (
                <>
                  <span className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary flex-shrink-0">
                    <Icon size={16} strokeWidth={2} />
                  </span>
                  <span className="leading-tight">
                    <span className="block text-sm text-foreground/90 font-medium">{it.t}</span>
                    <span className="block text-[0.7rem] text-foreground/45">{it.s}</span>
                  </span>
                </>
              );
              const cls = "glass rounded-xl border border-primary/12 px-4 py-2.5 flex items-center gap-3 transition-all duration-300";
              return it.to ? (
                <Link key={it.id} to={it.to} data-testid={`trust-badge-${it.id}`} className={`${cls} hover:border-primary/45 hover:-translate-y-1`}>
                  {inner}
                </Link>
              ) : (
                <div key={it.id} data-testid={`trust-badge-${it.id}`} className={`${cls} hover:border-primary/35 hover:-translate-y-1`}>
                  {inner}
                </div>
              );
            })}
          </div>
        </div>

        {/* legal links — rectangular glass tiles, matching Trust & Compliance */}
        <div className="pt-6 sm:pt-8 border-t border-white/5">
          <button
            type="button"
            onClick={() => setLegalOpen((o) => !o)}
            data-testid="footer-legal-toggle"
            className="md:hidden w-full flex items-center justify-between py-1 mb-3"
            aria-expanded={legalOpen}
          >
            <span className="uppercase tracking-[0.22em] text-[0.65rem] text-foreground/40">{t("landing.footer.legalLabel")}</span>
            <ChevronDown size={16} className={`text-foreground/40 transition-transform duration-300 ${legalOpen ? "rotate-180" : ""}`} />
          </button>
          <p className="hidden md:block text-center uppercase tracking-[0.24em] text-[0.65rem] text-foreground/35 mb-5 sm:mb-6">{t("landing.footer.legalLabel")}</p>
          <div className={`${legalOpen ? "grid" : "hidden"} md:grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5`}>
            {LEGAL_LINKS.map((l) => {
              const Icon = l.icon || FileText;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={l.id}
                  className="glass rounded-xl border border-primary/12 px-3.5 py-2.5 flex items-center gap-2.5 min-w-0 hover:border-primary/45 hover:-translate-y-0.5 transition-all duration-300"
                >
                  <span className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary flex-shrink-0">
                    <Icon size={15} strokeWidth={2} />
                  </span>
                  <span className="text-sm leading-tight text-foreground/80 min-w-0 break-words hyphens-auto">{t(l.key)}</span>
                </Link>
              );
            })}
          </div>
          <div className="flex justify-center md:justify-end mt-5">
            <LanguageSwitcher />
          </div>
        </div>

        {/* baseline / made in */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-5 sm:pt-6 border-t border-white/5 text-xs text-foreground/40">
          <span data-testid="footer-madein" className="inline-flex items-center gap-1.5">
            {t("landing.footer.madeIn")} <Heart size={12} className="text-primary fill-primary" /> {t("landing.footer.madeInPlace")}
            <span className="mx-2 text-foreground/20">·</span>
            <span className="tracking-wide">{t("landing.footer.langs")}</span>
          </span>
          <span>© {new Date().getFullYear()} COCOON MIND — {t("landing.footer.rights")}</span>
        </div>
      </div>
    </footer>
  );
}
